import React, { useCallback, useMemo, useState } from 'react';

import type { IntakeFormDefinition, IntakeFormFileValue, IntakeFormValues } from '@aline/common';
import {
  ALINE_TOOL_NAMES,
  IntakeFormDefinitionSchema,
  IntakeFormValuesSchema,
  validateIntakeFormValues,
  WorkflowLaunchTemplateInputSchema,
} from '@aline/common';
import { Divider, Grid, Skeleton, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useSnackbar } from 'notistack';
import { AccountsRolesAutoComplete } from 'src/components/AccountsRolesAutoComplete';
import { useChatAdapter } from 'src/components/ai/chat/ChatPanelMarkdown/ChatPanelMarkdown';
import { useGetUnassignedRoles } from 'src/components/AssignRoleList';
import type { UserDefinedRoleIdToDetails } from 'src/components/AssignRoleList';
import type { AssignRoleRowProps } from 'src/components/AssignRoleRow';
import { useAssignRoleRow } from 'src/components/AssignRoleRow';
import {
  AssignRoleHeader,
  AssignRoleListLoading,
} from 'src/components/quick-start/components/AssignRoles';
import { getShouldShowDocumentFieldsSection } from 'src/components/quick-start/components/launch-template-visibility';
import { LaunchDocumentFields } from 'src/components/quick-start/components/LaunchDocumentFieldsStep';
import { draftFieldsFromDefinition } from 'src/components/workflow/workflow-detail/intake-form/intake-form-fields';
import { IntakeFormFields } from 'src/components/workflow/workflow-detail/intake-form/IntakeFormFields';
import { Apollo } from 'src/gql/generated';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';
import { z } from 'zod';

import {
  ApprovalCard,
  CardPrimaryAction,
  CardSecondaryAction,
  PendingInteractionCardChrome,
} from './AskUser';
import { getInitialIntakeFormValues } from './WorkflowCollectIntakeFormCard';

const useStyles = makeStyles()(theme => ({
  assignAutocomplete: {
    maxWidth: 480,
  },
  error: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.palette.error.main,
    fontSize: 12,
  },
  intakeFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minWidth: 0,
    overflowX: 'hidden',
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  sectionTitle: {
    ...getTypographyForSx(theme.typography.subtitle1),
  },
  skeletonField: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
}));

/** Legacy client-input pause for in-flight collect threads. */
export interface WorkflowCollectLaunchInfoCardProps {
  messageId: string;
  suggestedIntakeValues: IntakeFormValues;
  templateWorkflowId: string;
  toolCallId: string;
  maxHeight?: number | null;
  suggestedAlineFieldValues?: Record<string, string>;
  suggestedRoleAssignments?: Record<string, string>;
}

/** Launch-template approval pause — collect requirements then approve to execute. */
export interface WorkflowLaunchTemplateApprovalCardProps {
  approvalId: string;
  input: unknown;
  messageId: string;
  maxHeight?: number | null;
}

type LaunchDocumentFieldsProps = React.ComponentProps<typeof LaunchDocumentFields>;

interface LaunchRoles {
  filteredRoleAssignments: ReturnType<typeof useGetUnassignedRoles>['filteredRoleAssignments'];
  getInitialValues: ReturnType<typeof useGetUnassignedRoles>['getInitialValues'];
  handleRowChange: ReturnType<typeof useGetUnassignedRoles>['handleRowChange'];
  roleAssignmentMap: UserDefinedRoleIdToDetails;
}

type LaunchSubmitMode =
  | { kind: 'clientTool'; messageId: string; toolCallId: string }
  | { approvalId: string; baseInput: unknown; kind: 'approval'; messageId: string };

/**
 * Loads the template's launch requirements, then mounts a keyed ready card so
 * answers seed once via useState. Apollo hands back fresh references on every
 * cache write (chat streaming writes constantly), so seeding from an effect
 * would wipe in-progress answers.
 */
export function WorkflowCollectLaunchInfoCard({
  maxHeight,
  messageId,
  suggestedAlineFieldValues = {},
  suggestedIntakeValues,
  suggestedRoleAssignments = {},
  templateWorkflowId,
  toolCallId,
}: WorkflowCollectLaunchInfoCardProps) {
  return (
    <LaunchInfoRequirementsLoader
      maxHeight={maxHeight}
      submitMode={{ kind: 'clientTool', messageId, toolCallId }}
      suggestedAlineFieldValues={suggestedAlineFieldValues}
      suggestedIntakeValues={suggestedIntakeValues}
      suggestedRoleAssignments={suggestedRoleAssignments}
      templateWorkflowId={templateWorkflowId}
    />
  );
}

export function WorkflowLaunchTemplateApprovalCard({
  approvalId,
  input,
  maxHeight,
  messageId,
}: WorkflowLaunchTemplateApprovalCardProps) {
  const parsed = WorkflowLaunchTemplateInputSchema.safeParse(input);
  if (!parsed.success) {
    return (
      <ApprovalCard
        approvalId={approvalId}
        input={input}
        maxHeight={maxHeight}
        messageId={messageId}
        toolName={ALINE_TOOL_NAMES.workflowLaunchTemplate}
      />
    );
  }

  return (
    <LaunchInfoRequirementsLoader
      maxHeight={maxHeight}
      submitMode={{ approvalId, baseInput: parsed.data, kind: 'approval', messageId }}
      suggestedAlineFieldValues={parsed.data.suggestedAlineFieldValues ?? {}}
      suggestedIntakeValues={parsed.data.suggestedIntakeValues ?? {}}
      suggestedRoleAssignments={parsed.data.suggestedRoleAssignments ?? {}}
      templateWorkflowId={parsed.data.templateWorkflowId}
    />
  );
}

function LaunchInfoRequirementsLoader({
  maxHeight,
  submitMode,
  suggestedAlineFieldValues,
  suggestedIntakeValues,
  suggestedRoleAssignments,
  templateWorkflowId,
}: {
  submitMode: LaunchSubmitMode;
  suggestedAlineFieldValues: Record<string, string>;
  suggestedIntakeValues: IntakeFormValues;
  suggestedRoleAssignments: Record<string, string>;
  templateWorkflowId: string;
  maxHeight?: number | null;
}) {
  const { classes } = useStyles();
  const {
    data: intakeData,
    error: intakeError,
    loading: intakeLoading,
  } = Apollo.useWorkflowIntakeFormQuery({
    variables: { workflowId: templateWorkflowId },
  });
  const {
    filteredRoleAssignments,
    getInitialValues,
    handleRowChange,
    loading: rolesLoading,
    roleAssignmentMap,
  } = useGetUnassignedRoles({
    entityId: templateWorkflowId,
    entityType: 'Workflow',
    filterBy: 'unassigned',
    // Seeds once on mount, so later suggestion-object churn cannot clobber edits.
    initialRoleAssignmentMap: getInitialRoleAssignmentMap(suggestedRoleAssignments),
    onListChange: () => undefined,
  });
  const template = intakeData?.workflow?.intakeFormTemplate ?? null;
  const parsedDefinition = useMemo(
    () => IntakeFormDefinitionSchema.safeParse(template?.formDefinition),
    [template?.formDefinition]
  );
  const parsedDefaults = useMemo(
    () => IntakeFormValuesSchema.safeParse(template?.defaultValues),
    [template?.defaultValues]
  );
  const intakeDefinition = parsedDefinition.success ? parsedDefinition.data : null;
  const alineFields = intakeData?.workflow?.intakeFields ?? [];
  const hasRoles = filteredRoleAssignments.length > 0;
  const hasDocumentFields = getShouldShowDocumentFieldsSection(alineFields.length);
  const hasIntake = Boolean(
    intakeDefinition && draftFieldsFromDefinition(intakeDefinition).length > 0
  );
  const hasRequirements = hasRoles || hasDocumentFields || hasIntake;

  if (intakeLoading || rolesLoading) {
    return (
      <PendingInteractionCardChrome
        expanded
        maxHeight={maxHeight}
        title={submitMode.kind === 'approval' ? 'Collect info and launch' : 'Launch information'}
      >
        <LaunchInfoLoading />
      </PendingInteractionCardChrome>
    );
  }

  if (intakeError) {
    return (
      <PendingInteractionCardChrome
        expanded
        maxHeight={maxHeight}
        title={submitMode.kind === 'approval' ? 'Collect info and launch' : 'Launch information'}
      >
        <Typography className={classes.error}>
          The template launch information could not be loaded. Please try again.
        </Typography>
      </PendingInteractionCardChrome>
    );
  }

  if (submitMode.kind === 'approval' && !hasRequirements) {
    return (
      <ApprovalCard
        approvalId={submitMode.approvalId}
        input={submitMode.baseInput}
        maxHeight={maxHeight}
        messageId={submitMode.messageId}
        toolName={ALINE_TOOL_NAMES.workflowLaunchTemplate}
      />
    );
  }

  return (
    <ReadyWorkflowCollectLaunchInfoCard
      key={submitMode.kind === 'clientTool' ? submitMode.toolCallId : submitMode.approvalId}
      alineFields={alineFields}
      documentsList={
        intakeData?.workflow?.files.map(({ document }) => ({
          documentId: document.id,
          name: document.name,
        })) ?? []
      }
      initialAlineFieldValues={Object.entries(suggestedAlineFieldValues).map(
        ([fieldId, value]) => ({ fieldId, value })
      )}
      initialIntakeValues={
        intakeDefinition
          ? getInitialIntakeFormValues(intakeDefinition, parsedDefaults, suggestedIntakeValues)
          : {}
      }
      intakeDefinition={intakeDefinition}
      maxHeight={maxHeight}
      roles={{ filteredRoleAssignments, getInitialValues, handleRowChange, roleAssignmentMap }}
      submitMode={submitMode}
    />
  );
}

function ReadyWorkflowCollectLaunchInfoCard({
  alineFields,
  documentsList,
  initialAlineFieldValues,
  initialIntakeValues,
  intakeDefinition,
  maxHeight,
  roles,
  submitMode,
}: {
  alineFields: LaunchDocumentFieldsProps['alineFields'];
  documentsList: LaunchDocumentFieldsProps['documentsList'];
  initialAlineFieldValues: Apollo.AlineFieldValueInput[];
  initialIntakeValues: IntakeFormValues;
  intakeDefinition: IntakeFormDefinition | null;
  roles: LaunchRoles;
  submitMode: LaunchSubmitMode;
  maxHeight?: number | null;
}) {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { addToolApprovalResponse, addToolOutput } = useChatAdapter();
  const [expanded, setExpanded] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alineFieldValues, setAlineFieldValues] = useState(initialAlineFieldValues);
  const [intakeValues, setIntakeValues] = useState(initialIntakeValues);
  const [deleteDocuments] = Apollo.useDeleteDocumentsMutation();
  const { filteredRoleAssignments, getInitialValues, handleRowChange, roleAssignmentMap } = roles;
  const intakeFields = useMemo(
    () => (intakeDefinition ? draftFieldsFromDefinition(intakeDefinition) : []),
    [intakeDefinition]
  );
  const intakeValidation = intakeDefinition
    ? validateIntakeFormValues(intakeDefinition, intakeValues)
    : null;
  const valueByFieldId = useMemo(
    () => new Map(alineFieldValues.map(entry => [entry.fieldId, entry.value])),
    [alineFieldValues]
  );
  const hasRoles = filteredRoleAssignments.length > 0;
  const hasDocumentFields = getShouldShowDocumentFieldsSection(alineFields.length);
  const hasIntake = intakeFields.length > 0;
  const submitDisabledReason = getSubmitDisabledReason({ intakeValidation, submitting });
  const isApproval = submitMode.kind === 'approval';

  const removeFile = useCallback(
    async (file: IntakeFormFileValue) => {
      await deleteDocuments({ variables: { input: { documentIds: [file.documentId] } } });
    },
    [deleteDocuments]
  );

  const buildCollectedPayload = () => ({
    alineFieldValues: Object.fromEntries(
      alineFieldValues
        .filter(entry => entry.value.trim() !== '')
        .map(entry => [entry.fieldId, entry.value])
    ),
    instanceAssignments: Object.entries(roleAssignmentMap).map(([userDefinedRoleId, details]) => ({
      ...details,
      userDefinedRoleId,
    })),
    ...(intakeValidation ? { intakeFormValues: intakeValidation.sanitizedValues } : {}),
  });

  const submit = async () => {
    if (submitDisabledReason) return;
    try {
      setSubmitting(true);
      const collected = buildCollectedPayload();
      if (submitMode.kind === 'approval') {
        const base =
          typeof submitMode.baseInput === 'object' && submitMode.baseInput !== null
            ? submitMode.baseInput
            : {};

        await addToolApprovalResponse({
          approved: true,
          id: submitMode.approvalId,
          input: { ...base, ...collected },
          messageId: submitMode.messageId,
        });
        return;
      }

      await addToolOutput({
        messageId: submitMode.messageId,
        output: { ...collected, status: 'submitted' as const },
        tool: 'workflowCollectLaunchInfo',
        toolCallId: submitMode.toolCallId,
      });
    } catch (error) {
      Sentry.captureException(error);
      enqueueSnackbar(
        isApproval
          ? 'Failed to launch the workflow. Please try again.'
          : 'Failed to submit launch information. Please try again.',
        { variant: 'error' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const skip = async () => {
    if (submitting) return;
    try {
      setSubmitting(true);
      if (submitMode.kind === 'approval') {
        await addToolApprovalResponse({
          approved: false,
          id: submitMode.approvalId,
          messageId: submitMode.messageId,
        });
        return;
      }

      await addToolOutput({
        messageId: submitMode.messageId,
        output: { status: 'skipped' },
        tool: 'workflowCollectLaunchInfo',
        toolCallId: submitMode.toolCallId,
      });
    } catch (error) {
      Sentry.captureException(error);
      enqueueSnackbar(
        isApproval
          ? 'Failed to skip the launch. Please try again.'
          : 'Failed to skip launch information. Please try again.',
        { variant: 'error' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PendingInteractionCardChrome
      expanded={expanded}
      footer={
        <>
          <CardSecondaryAction disabled={submitting} onClick={() => void skip()}>
            Skip
          </CardSecondaryAction>
          <CardPrimaryAction
            disabled={submitting}
            disabledReason={submitDisabledReason}
            loading={submitting}
            onClick={() => void submit()}
          >
            {isApproval ? 'Launch' : 'Submit'}
          </CardPrimaryAction>
        </>
      }
      maxHeight={maxHeight}
      title={isApproval ? 'Collect info and launch' : 'Launch information'}
      toggleLabel={isApproval ? 'launch approval' : 'launch information'}
      onToggleExpanded={() => setExpanded(current => !current)}
    >
      <div className={classes.root}>
        {hasRoles && (
          <LaunchRolesSection
            assignments={filteredRoleAssignments}
            getInitialValues={getInitialValues}
            roleAssignmentMap={roleAssignmentMap}
            onChange={handleRowChange}
          />
        )}
        {hasIntake && intakeDefinition && (
          <section className={classes.section}>
            <Typography className={classes.sectionTitle}>Intake questions</Typography>
            <div className={classes.intakeFields}>
              <IntakeFormFields
                fields={intakeFields}
                formDefinition={intakeDefinition}
                values={intakeValues}
                onChange={(name, value) =>
                  setIntakeValues(current => ({ ...current, [name]: value }))
                }
                onRemoveFile={removeFile}
              />
            </div>
          </section>
        )}
        {hasDocumentFields && (
          <section className={classes.section}>
            <Typography className={classes.sectionTitle}>Document fields</Typography>
            <LaunchDocumentFields
              alineFields={alineFields}
              documentsList={documentsList}
              valueByFieldId={valueByFieldId}
              onChange={(fieldId, value) =>
                setAlineFieldValues(current => upsertAlineFieldValue(current, fieldId, value))
              }
            />
          </section>
        )}
      </div>
    </PendingInteractionCardChrome>
  );
}

function LaunchRolesSection({
  assignments,
  getInitialValues,
  onChange,
  roleAssignmentMap,
}: {
  assignments: ReturnType<typeof useGetUnassignedRoles>['filteredRoleAssignments'];
  getInitialValues: ReturnType<typeof useGetUnassignedRoles>['getInitialValues'];
  onChange: ReturnType<typeof useGetUnassignedRoles>['handleRowChange'];
  roleAssignmentMap: UserDefinedRoleIdToDetails;
}) {
  const { classes } = useStyles();
  return (
    <section className={classes.section}>
      <Typography className={classes.sectionTitle}>Assign roles</Typography>
      <AssignRoleHeader />
      <Divider />
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        {assignments.map(assignment => (
          <LaunchAssignRoleRow
            key={assignment.assigneeRole.id}
            assignment={assignment}
            initialUserOrEmail={getInitialValues(roleAssignmentMap[assignment.assigneeRole.id])}
            onChange={onChange}
          />
        ))}
      </Grid>
    </section>
  );
}

function LaunchAssignRoleRow({ assignment, initialUserOrEmail, onChange }: AssignRoleRowProps) {
  const { classes } = useStyles();
  const { handleAutocompleteChange, userOrSuggested } = useAssignRoleRow({
    assignment,
    initialUserOrEmail,
    onChange,
  });

  return (
    <>
      <Grid size={3}>
        <Typography variant="body1">
          {assignment.assigneeRole.roleName || 'Untitled Role'}:
        </Typography>
      </Grid>
      <Grid size={9}>
        <AccountsRolesAutoComplete
          suggestInviteEmail
          className={classes.assignAutocomplete}
          includeOptions="users"
          placeholder="Search users or type an email..."
          value={userOrSuggested}
          variant="avatar"
          onChange={handleAutocompleteChange}
        />
      </Grid>
    </>
  );
}

function LaunchInfoLoading() {
  const { classes } = useStyles();
  return (
    <div aria-busy="true" className={classes.intakeFields}>
      <AssignRoleListLoading />
      <div className={classes.skeletonField}>
        <Skeleton height={14} variant="text" width="44%" />
        <Skeleton height={36} variant="rounded" width="100%" />
      </div>
      <div className={classes.skeletonField}>
        <Skeleton height={14} variant="text" width="56%" />
        <Skeleton height={36} variant="rounded" width="100%" />
      </div>
    </div>
  );
}

function getSubmitDisabledReason({
  intakeValidation,
  submitting,
}: {
  intakeValidation: ReturnType<typeof validateIntakeFormValues> | null;
  submitting: boolean;
}) {
  if (submitting) return 'Submitting...';
  if (!intakeValidation) return '';
  if (intakeValidation.missingRequiredFields.length > 0)
    return 'Answer required questions before submitting';
  if (intakeValidation.fieldErrors && Object.keys(intakeValidation.fieldErrors).length > 0)
    return 'Fix invalid fields before submitting';
  return '';
}

/**
 * The agent's role hints are free text, and only an email can be turned into a
 * concrete assignment without a user lookup. Name-only hints are dropped so the
 * submitted assignments never contain a value the launch mutation would reject.
 */
function getInitialRoleAssignmentMap(
  suggestedRoleAssignments: Record<string, string>
): UserDefinedRoleIdToDetails {
  const entries = Object.entries(suggestedRoleAssignments).flatMap<[string, { email: string }]>(
    ([userDefinedRoleId, hint]) => {
      const email = z.email().safeParse(hint.trim().toLowerCase());
      if (!z.uuid().safeParse(userDefinedRoleId).success || !email.success) return [];
      return [[userDefinedRoleId, { email: email.data }]];
    }
  );
  return Object.fromEntries(entries);
}

function upsertAlineFieldValue(
  current: Apollo.AlineFieldValueInput[],
  fieldId: string,
  value: string
): Apollo.AlineFieldValueInput[] {
  const without = current.filter(entry => entry.fieldId !== fieldId);
  if (value === '') return without;
  return [...without, { fieldId, value }];
}
